// `!== undefined` (not `||`) so an explicitly empty value ("" — set by
// the Docker frontend image when it's served from the same Nginx
// origin as the backend, see frontend/Dockerfile) is honored as "use
// relative/same-origin URLs" rather than falling through to the
// localhost default. Unset (local dev without this env var) still
// falls back to localhost:8000 as before.
export const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL !== undefined
    ? process.env.REACT_APP_API_BASE_URL
    : 'http://localhost:8000';

function deriveSameOriginWsBaseUrl() {
  if (typeof window === 'undefined') return '';
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}`;
}

// WebSocket URLs need an absolute ws(s):// origin (unlike the fetch
// paths above, a relative string doesn't work as a WebSocket URL), so
// an explicitly empty value derives one from the browser's current
// location instead — ws over http, wss over https.
export const WS_BASE_URL =
  process.env.REACT_APP_WS_BASE_URL !== undefined
    ? process.env.REACT_APP_WS_BASE_URL || deriveSameOriginWsBaseUrl()
    : 'ws://localhost:8000';

// Endpoints that must never trigger the refresh-and-retry flow: a 401
// from these means "bad credentials" / "not logged in yet", not "your
// session expired mid-use". In particular /api/auth/refresh itself is
// exempt so a failed refresh can never recursively try to refresh again.
const AUTH_EXEMPT_PATHS = new Set([
  '/api/auth/login',
  '/api/auth/register',
  // A 401 here means "that Google identity was not accepted", not
  // "your session expired" - without this the client would fire a
  // refresh and retry the sign-in it just failed.
  '/api/auth/google',
  // A 401/403 from these means the code or the account was rejected,
  // never "your session expired" - there is no session yet to refresh.
  '/api/auth/verify-email',
  '/api/auth/resend-verification',
  '/api/auth/logout',
  '/api/auth/refresh',
  '/api/auth/forgot-password',
  '/api/auth/reset-password',
]);

let unauthorizedHandler = null;

// Registered by AuthContext so a failed refresh can clear the app's
// session state reactively (ProtectedRoute then redirects to /login on
// its own re-render — no imperative navigation needed here).
export function setUnauthorizedHandler(fn) {
  unauthorizedHandler = fn;
}

// Shared in-flight refresh promise: concurrent 401s from several
// simultaneous requests all await this SAME promise instead of each
// firing their own POST /api/auth/refresh ("refresh storm").
//
// Access/refresh cookies are httpOnly (deliberately unreadable from JS,
// see routers/auth.py) so there is no cookie value to compare across
// requests to detect "someone else already refreshed". Instead, the
// resolved promise is kept alive for a short cooldown window after it
// settles: several requests that hit 401 within a few hundred ms of
// each other (normal for a page firing a handful of requests at once)
// all land on the same settled promise and resolve instantly instead of
// each starting a new refresh.
let refreshInFlight = null;
const REFRESH_DEDUPE_WINDOW_MS = 250;
// After a refresh that could not be ANSWERED (see below), further requests
// get the same answer for this long instead of each trying the struggling
// endpoint again.
const REFRESH_UNAVAILABLE_COOLDOWN_MS = 2000;

// What a refresh attempt established. They are three different facts:
//   ok          - new cookies were issued; retry the request.
//   rejected    - the server ANSWERED and refused (the refresh token is
//                 expired, revoked, or the account is gone). The session is
//                 over: this is the only outcome that signs anybody out.
//   unavailable - the server did not give an answer that says anything about
//                 the session: a network failure, a timeout, 408, 429, a 5xx,
//                 or a page that is not the API. The session may be perfectly
//                 valid, so it is kept and the request fails recoverably.
const REFRESH_OK = 'ok';
const REFRESH_REJECTED = 'rejected';
const REFRESH_UNAVAILABLE = 'unavailable';

// Mirrors the desktop app (desktop/core/src/api.rs refresh_failure_is_
// transient): these statuses say the service could not answer, not that the
// refresh token is bad. Every other non-2xx is a rejection.
function isTransientRefreshStatus(status) {
  return status === 408 || status === 429 || (status >= 500 && status <= 599);
}

async function classifyRefreshResponse(res) {
  if (isTransientRefreshStatus(res.status)) {
    return REFRESH_UNAVAILABLE;
  }

  if (!res.ok) {
    return REFRESH_REJECTED;
  }

  // 2xx. The API answers with JSON (or nothing); an HTML page here is a
  // proxy or the static frontend answering instead of the API, which
  // issued no cookies and says nothing about the session.
  const { parseFailed } = await readJsonBody(res);
  return parseFailed ? REFRESH_UNAVAILABLE : REFRESH_OK;
}

function refreshAccessToken() {
  if (!refreshInFlight) {
    refreshInFlight = fetch(`${API_BASE_URL}/api/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    })
      .then(classifyRefreshResponse)
      .catch(() => REFRESH_UNAVAILABLE)
      .then((outcome) => {
        setTimeout(
          () => {
            refreshInFlight = null;
          },
          outcome === REFRESH_UNAVAILABLE
            ? REFRESH_UNAVAILABLE_COOLDOWN_MS
            : REFRESH_DEDUPE_WINDOW_MS
        );
        return outcome;
      });
  }

  return refreshInFlight;
}

// Raised instead of calling the unauthorized handler when the refresh could
// not be answered. Shaped like the other transport errors (isNetworkError)
// so every screen that already handles "try again" handles it.
function refreshUnavailableError() {
  const error = new Error('Unable to refresh your session right now. Please try again.');
  error.isNetworkError = true;
  error.refreshUnavailable = true;
  return error;
}

// An empty body is legitimate (DELETE /api/notifications/{id} answers
// 204) and reads as null. A body that IS present but doesn't parse as
// JSON is a different thing entirely: it means the request never
// reached the API - typically the static frontend answering /api/...
// with index.html because Nginx doesn't proxy /api on that origin, or
// REACT_APP_API_BASE_URL was left blank for a split frontend/api-domain
// deployment (see .env.deploy). Telling the two apart is what
// `parseFailed` is for: without it a 200 full of HTML silently became
// `null`, and the app only fell over much later and somewhere else -
// "Cannot read properties of null (reading 'filter')" in a page that
// was simply rendering the list it asked for.
async function readJsonBody(response) {
  let text = '';

  try {
    text = await response.text();
  } catch (_) {
    return { data: null, parseFailed: false };
  }

  if (!text.trim()) {
    return { data: null, parseFailed: false };
  }

  try {
    return { data: JSON.parse(text), parseFailed: false };
  } catch (_) {
    return { data: null, parseFailed: true };
  }
}

async function performFetch(path, method, body) {
  let response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      credentials: 'include',
      headers: body ? { 'Content-Type': 'application/json' } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (cause) {
    const error = new Error('Unable to load data. Please try again.');
    error.isNetworkError = true;
    error.cause = cause;
    throw error;
  }

  return { response, ...(await readJsonBody(response)) };
}

function throwApiError(response, data) {
  const rawDetail = data && data.detail;
  const topLevelMessage = data && typeof data.message === 'string' ? data.message : null;
  const topLevelCode = data && typeof data.code === 'string' ? data.code : undefined;
  const topLevelDetails =
    data && Object.prototype.hasOwnProperty.call(data, 'details') ? data.details : undefined;

  // Some endpoints send a structured detail with a machine code and a
  // human-readable message. Accept both { detail, code } and
  // { message, code } shapes while leaving plain-string details unchanged.
  // Newer API errors also expose that same contract at the top level:
  // { detail, code, message, details }.
  const isStructuredDetail =
    rawDetail &&
    typeof rawDetail === 'object' &&
    (typeof rawDetail.detail === 'string' || typeof rawDetail.message === 'string');

  const message = topLevelMessage || (isStructuredDetail
    ? rawDetail.detail || rawDetail.message
    : rawDetail || `Request failed with status ${response.status}`);

  const error = new Error(
    typeof message === 'string' ? message : JSON.stringify(message)
  );

  error.status = response.status;
  error.code = topLevelCode || (isStructuredDetail ? rawDetail.code : undefined);
  error.details =
    topLevelDetails !== undefined
      ? topLevelDetails
      : isStructuredDetail
        ? rawDetail.details
        : undefined;
  throw error;
}

// Deliberately not a generic "something went wrong": this failure has
// exactly one class of cause (the request was answered by something
// that isn't the API) and the message names it, so it is diagnosable
// from a screenshot of the console.
function throwNonJsonError(path, response) {
  const error = new Error('Unable to load data. Please try again.');

  error.status = response.status;
  error.isNetworkError = true;
  throw error;
}

export async function apiRequest(path, { method = 'GET', body } = {}, _retry = false) {
  const { response, data, parseFailed } = await performFetch(path, method, body);

  if (response.status === 401 && !AUTH_EXEMPT_PATHS.has(path) && !_retry) {
    const refreshed = await refreshAccessToken();

    if (refreshed === REFRESH_OK) {
      // Retry the original request exactly once with the new access
      // cookie. _retry=true guarantees this can never loop again even
      // if the retried request also comes back 401.
      return apiRequest(path, { method, body }, true);
    }

    if (refreshed === REFRESH_UNAVAILABLE) {
      throw refreshUnavailableError();
    }

    if (unauthorizedHandler) {
      unauthorizedHandler();
    }
  }

  if (!response.ok) {
    throwApiError(response, data);
  }

  if (parseFailed) {
    throwNonJsonError(path, response);
  }

  return data;
}

// FormData needs its own fetch: performFetch always JSON.stringifies
// body and sets Content-Type: application/json, which would corrupt a
// file upload. No Content-Type header here is deliberate - the browser
// sets multipart/form-data with the right boundary itself only when it
// isn't overridden.
async function performUploadFetch(path, formData) {
  let response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
  } catch (cause) {
    const error = new Error('Unable to load data. Please try again.');
    error.isNetworkError = true;
    error.cause = cause;
    throw error;
  }

  return { response, ...(await readJsonBody(response)) };
}

export async function apiUpload(path, formData, _retry = false) {
  const { response, data, parseFailed } = await performUploadFetch(path, formData);

  if (response.status === 401 && !_retry) {
    const refreshed = await refreshAccessToken();

    if (refreshed === REFRESH_OK) {
      return apiUpload(path, formData, true);
    }

    if (refreshed === REFRESH_UNAVAILABLE) {
      throw refreshUnavailableError();
    }

    if (unauthorizedHandler) {
      unauthorizedHandler();
    }
  }

  if (!response.ok) {
    throwApiError(response, data);
  }

  if (parseFailed) {
    throwNonJsonError(path, response);
  }

  return data;
}

// A binary GET (e.g. an uploaded document's original file). Same
// cookie auth and refresh-once-on-401 flow as apiRequest, but the body
// is returned as a Blob instead of being parsed as JSON. Error
// responses are still JSON, so those go through throwApiError as usual.
async function fetchBlobWithMetadata(path, _retry = false) {
  let response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, { credentials: 'include' });
  } catch (cause) {
    const error = new Error('Unable to load data. Please try again.');
    error.isNetworkError = true;
    error.cause = cause;
    throw error;
  }

  if (response.status === 401 && !_retry) {
    const refreshed = await refreshAccessToken();

    if (refreshed === REFRESH_OK) {
      return fetchBlobWithMetadata(path, true);
    }

    if (refreshed === REFRESH_UNAVAILABLE) {
      throw refreshUnavailableError();
    }

    if (unauthorizedHandler) {
      unauthorizedHandler();
    }
  }

  if (!response.ok) {
    const { data } = await readJsonBody(response);
    throwApiError(response, data);
  }

  // A 200 full of HTML means the static frontend answered instead of
  // the API - see throwNonJsonError.
  if ((response.headers.get('content-type') || '').includes('text/html')) {
    throwNonJsonError(path, response);
  }

  const blob = await response.blob();
  return {
    blob,
    filename: parseContentDispositionFilename(
      response.headers.get('content-disposition')
    ),
    contentType: response.headers.get('content-type') || '',
  };
}

// Backward-compatible shape for document callers that expect a bare Blob.
export async function apiBlob(path, _retry = false) {
  const { blob } = await fetchBlobWithMetadata(path, _retry);
  return blob;
}

// Content-Disposition per RFC 6266/5987. Prefer filename*=UTF-8 so a
// backend-canonical Gujarati/Hindi filename survives the ASCII header
// transport; quoted filename= remains the compatibility fallback.
function parseContentDispositionFilename(headerValue) {
  if (!headerValue) return null;

  const encodedMatch = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(headerValue);
  if (encodedMatch) {
    try {
      return decodeURIComponent(encodedMatch[1].trim());
    } catch (_) {
      // Fall through to the ASCII filename when encoding is malformed.
    }
  }

  const match = /filename="([^"]+)"/.exec(headerValue);
  return match ? match[1] : null;
}

// A binary GET that also needs a server-supplied filename (e.g. meeting
// summary PDF export - services/meetingApi.js's
// exportMeetingSummaryPdf). Deliberately a separate function rather than
// changing apiBlob's return shape: apiBlob already has callers (document
// downloads) that expect a bare Blob, and this project's own engineering
// rules call for not breaking them. Same cookie auth / 401-refresh-retry
// / HTML-fallback detection as apiBlob above.
export async function apiBlobWithHeaders(path, _retry = false) {
  return fetchBlobWithMetadata(path, _retry);
}
